import React, { useEffect, useState, useMemo } from 'react';
import { getProcessingEmails } from '../api/api';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from "./ui/avatar"
import { Search, Clock, Activity, Inbox, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import dayjs from 'dayjs';

const AVATAR_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-orange-500', 'bg-purple-500'];

function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % AVATAR_COLORS.length;
    return AVATAR_COLORS[colorIndex];
}

export default function ProcessingEmails() {
    const [emails, setEmails] = useState([]);
    const [page] = useState(0);
    const [rowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    // Ortalama gönderim süresi (örnek: 45 saniye)
    const AVG_SEND_SEC = 45;
    const estimatedTotalSec = total * AVG_SEND_SEC;
    const estimatedFinish = new Date(Date.now() + estimatedTotalSec * 1000);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            const filters = { search };
            const res = await getProcessingEmails(page + 1, rowsPerPage, filters);
            setEmails(res.data.emails);
            setTotal(res.data.total);
            setLoading(false);
        }
        fetchData();
    }, [page, rowsPerPage, search]);

    const filteredEmails = useMemo(() => emails, [emails]);

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            <div>
                <h2 className="text-3xl font-bold tracking-tight">Processing Emails</h2>
                <p className="text-muted-foreground mt-1">
                    Emails currently being processed for sending.
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-8 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Processing
                        </CardTitle>
                        <Activity className="h-4 w-4 text-blue-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{total}</div>
                        <p className="text-xs text-muted-foreground">Emails in active batch</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-8 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Avg. Time per Email
                        </CardTitle>
                        <Clock className="h-4 w-4 text-amber-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{AVG_SEND_SEC}s</div>
                        <p className="text-xs text-muted-foreground">Estimated send time</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-8 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Est. Completion
                        </CardTitle>
                        <Clock className="h-4 w-4 text-emerald-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {estimatedFinish.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <p className="text-xs text-muted-foreground">Based on current load</p>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <div className="flex items-center justify-between gap-4">
                <div className="relative w-full md:w-80">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search processing emails..."
                        className="pl-8"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => {
                    setLoading(true);
                    setSearch(search); // Trigger reload
                }}>
                    <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                </Button>
            </div>

            {/* Table */}
            <div className="rounded-md border bg-card">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>First Name</TableHead>
                            <TableHead>Last Name</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created At</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-64 text-center">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <Activity className="h-8 w-8 text-primary animate-pulse opacity-20" />
                                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground animate-pulse">Syncing active batches...</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : filteredEmails.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-64 text-center">
                                    <div className="flex flex-col items-center justify-center gap-4 text-muted-foreground">
                                        <div className="h-16 w-16 rounded-full bg-muted/30 flex items-center justify-center">
                                            <Inbox className="h-8 w-8 opacity-20" />
                                        </div>
                                        <div className="max-w-xs mx-auto">
                                            <p className="font-bold text-foreground mb-1 uppercase text-xs tracking-wider">No active processing</p>
                                            <p className="text-[11px] opacity-60">All queued emails have been dispatched or the search query returned no matches in the current batch.</p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredEmails.map((row) => (
                                <TableRow key={row._id}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <Avatar className="h-8 w-8">
                                                <AvatarFallback className={`${stringToColor(row.email || 'U')} text-white`}>
                                                    {row.firstName?.[0] || row.email?.[0] || 'U'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="font-medium">{row.email}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell>{row.firstName}</TableCell>
                                    <TableCell>{row.lastName}</TableCell>
                                    <TableCell>{row.company}</TableCell>
                                    <TableCell>
                                        <Badge variant={row.isSend ? "success" : "secondary"} className={
                                            row.isProcessing ? "bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200" : ""
                                        }>
                                            {row.isSend ? "Sent" : (row.status === 'error' ? "Error" : (row.isProcessing ? 'Processing' : 'Queued'))}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{dayjs(row.createdAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
            {/* Pagination (Simplified) */}
            {/* Note: Ideally implementation of Shadcn Pagination, but basic buttons work too if Pagination component not created */}
        </div>
    );
} 